import React, { useMemo } from "react";
import { Alert, Autocomplete, Box, Checkbox, Grid, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { canEditShifts } from "app/shared/downloads/permissions";
import { describeShifts } from "app/shared/reports/WorkShiftsEditor";

export const DEFAULT_PARAMS = { field: "", aggregation: "max", columns: "hours", range_min: "", range_max: "", device_ids: [] };

// Con las opciones de un local (o de la empresa): conserva la variable si sigue existiendo, si no la de por defecto, y
// descarta los equipos que ya no corresponden.
export const prepareParams = (options, params) => {
    const variables = options?.variables || [];
    const field = variables.some((variable) => variable.field === params.field) ? params.field
        : (variables.find((variable) => variable.field === options?.default_field) || variables[0])?.field || "";
    const deviceIds = (options?.devices || []).filter((device) => device.fields.includes(field)).map((device) => device.id);
    return { ...params, field, device_ids: (params.device_ids || []).filter((id) => deviceIds.includes(id)) };
};

export const shiftsMissing = (options, params) => params.columns === "shifts" && !(options?.shifts || []).length;

export const canBuild = (options, params) => !!params.field && !shiftsMissing(options, params);

// Los campos de la planilla de temperaturas, sin el local ni las fechas: los usan el reporte rápido y el editor de
// plantillas. Sin equipos (showDevices=false) van todos los del local.
const TemperatureSheetFields = ({ options, value, onChange, showDevices = true }) => {
    const { authUser } = useJumboAuth();
    const devicesWithField = useMemo(
        () => (options?.devices || []).filter((device) => device.fields.includes(value.field)),
        [options, value.field],
    );

    return (
        <Box>
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <TextField select fullWidth label="Variable" value={value.field} disabled={!options?.variables?.length}
                               onChange={(event) => onChange({ field: event.target.value, device_ids: [] })}>
                        {(options?.variables || []).map((variable) => (
                            <MenuItem key={variable.field} value={variable.field}>{variable.label}{variable.unit ? ` (${variable.unit})` : ""}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField select fullWidth label="Valor de cada celda" value={value.aggregation} onChange={(event) => onChange({ aggregation: event.target.value })}>
                        {(options?.aggregations || []).map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                    </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                    <ToggleButtonGroup exclusive fullWidth size="small" value={value.columns} sx={{ height: "100%" }}
                                       onChange={(event, columns) => columns && onChange({ columns })}>
                        {(options?.column_modes || []).map((mode) => <ToggleButton key={mode.value} value={mode.value}>{mode.label}</ToggleButton>)}
                    </ToggleButtonGroup>
                </Grid>
                <Grid item xs={6} md={2}>
                    <TextField fullWidth label="Rango desde" placeholder="Ej: 2" value={value.range_min ?? ""} onChange={(event) => onChange({ range_min: event.target.value })} />
                </Grid>
                <Grid item xs={6} md={2}>
                    <TextField fullWidth label="Rango hasta" placeholder="Ej: 8" value={value.range_max ?? ""} onChange={(event) => onChange({ range_max: event.target.value })} />
                </Grid>
                {showDevices && (
                    <Grid item xs={12} md={8}>
                        <Autocomplete
                            multiple disableCloseOnSelect options={devicesWithField}
                            value={devicesWithField.filter((device) => value.device_ids.includes(device.id))}
                            getOptionLabel={(option) => option.name}
                            onChange={(event, devices) => onChange({ device_ids: devices.map((device) => device.id) })}
                            renderOption={(props, option, { selected }) => (
                                <li {...props}><Checkbox size="small" checked={selected} sx={{ mr: 1 }} />{option.name}{option.zone ? ` · ${option.zone}` : ""}</li>
                            )}
                            renderInput={(inputParams) => (
                                <TextField {...inputParams} label="Equipos"
                                           helperText={value.device_ids.length ? null : `Si no eliges ninguno, van todos (${devicesWithField.length}).`} />
                            )}
                        />
                    </Grid>
                )}
            </Grid>

            {value.columns === "shifts" && (
                <Box sx={{ mt: 1.5 }}>
                    {shiftsMissing(options, value) ? (
                        <Alert severity="warning">
                            {showDevices ? "Este local no tiene turnos. " : "La empresa no tiene turnos. "}
                            {canEditShifts(authUser)
                                ? <>Defínelos en <RouterLink to="/company_settings">Configuración</RouterLink> o en el menú del local.</>
                                : "Pídele a un administrador que los defina."}
                        </Alert>
                    ) : (
                        <Typography variant="caption" color="text.secondary">
                            Turnos {options?.shifts_source === "store" ? "de este local" : "de la empresa"}: {describeShifts(options?.shifts)}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default TemperatureSheetFields;
